/**
 * useSessionSSE — connects to SSE stream for a session and pushes messages
 * into the React Query cache in real-time.
 *
 * Uses fetch + ReadableStream because EventSource doesn't support custom
 * headers (we need x-tenant and Authorization).
 */

import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { API_URL } from "@/config";
import { streamHeaders } from "@/helpers/api_helper";
import type { SessionMessage, SessionDetailResponse } from "./types";
import { workflowKeys } from "./useWorkflowQueries";

const WF_BASE = "/api/v1/workflow/dashboard";

export function useSessionSSE(sessionId: string | null) {
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const connect = useCallback(() => {
    if (!sessionId) return;

    // Abort any existing connection
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    const url = `${API_URL}${WF_BASE}/sessions/${sessionId}/stream`;

    fetch(url, {
      headers: { ...streamHeaders },
      signal: controller.signal,
    })
      .then(async response => {
        if (!response.ok || !response.body) return;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        let keepPolling = true;
        while (keepPolling) {
          const { done, value } = await reader.read();
          if (done) { keepPolling = false; break; }

          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE lines
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            try {
              const msg: SessionMessage = JSON.parse(line.slice(6));
              if ("error" in msg) continue;

              // Merge into React Query cache
              queryClient.setQueryData<SessionDetailResponse>(
                workflowKeys.sessionDetail(sessionId),
                prev => {
                  if (!prev) return prev;
                  const exists = prev.messages.some(m => m.id === msg.id);
                  if (exists) return prev;
                  return {
                    ...prev,
                    messages: [...prev.messages, msg],
                  };
                }
              );
            } catch {
              // Ignore malformed events
            }
          }
        }

        // Stream ended normally — reconnect after a short delay
        if (!controller.signal.aborted) {
          reconnectTimer.current = setTimeout(connect, 3000);
        }
      })
      .catch(() => {
        // Network error or abort — reconnect if not intentionally aborted
        if (!controller.signal.aborted) {
          reconnectTimer.current = setTimeout(connect, 5000);
        }
      });
  }, [sessionId, queryClient]);

  useEffect(() => {
    connect();

    return () => {
      abortRef.current?.abort();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connect]);
}
